import dayjs from 'dayjs';
import { FIELD_NAMES } from './BasicSettings';
import get from 'lodash/get';
import { toast } from 'chameleon/ui-stack/ui/toast';
import { FIELD_NAMES as ADVANCED_FIELD_NAMES } from 'chameleon/legacy/src/components/assessment/AssignItem/components/AssignModal/Form/AdvancedSettings';
import isEmpty from 'lodash/isEmpty';

export const dateFormat = 'YYYY-MM-DDTHH:mm:ssZ';
export const notificationActionText = 'Manage Assignment';
export const HARD_TIMER_FIELD = 'is_hard_timer';

const formatDateWithFallback = (
  date,
  currentDateAndTime,
  printToggle,
  timezone,
  dateField,
) => {
  if (printToggle || !date) {
    return null;
  }

  const dateInTimezone = dayjs(date).tz(timezone);
  const isPastDate = dateInTimezone.isBefore(currentDateAndTime);

  if (isPastDate) {
    /**
     * Add 1 minute to the due date in order to handle the edge case where
     * both start date and due date are in the past.
     * Why 1 minute? - handle the possible cases where the dates could
     * be compared by minutes in the BE.
     * */
    const fallbackDate =
      dateField === FIELD_NAMES.DUE_AT
        ? currentDateAndTime.add(1, 'minute')
        : currentDateAndTime;

    return fallbackDate.format(dateFormat);
  }

  return dateInTimezone.format(dateFormat);
};

const isFullClass = (subjectInfo, sectionId, selectedStudents) => {
  const studentsInClass =
    subjectInfo.classes.find((cls) => cls.id === sectionId)?.students || [];
  return studentsInClass.length === selectedStudents.length;
};

const transformAdvancedSettings = (advancedSettings, isOffline) => {
  const isOnline = !isOffline;
  const {
    has_immediate_feedback,
    immediate_feedback_attempts,
    scramble_questions,
  } = advancedSettings;

  const isSecure = isOnline
    ? advancedSettings[ADVANCED_FIELD_NAMES.LOCKDOWN_BROWSER]
    : false;
  const hasImmediateFeedback = isOnline ? has_immediate_feedback : false;

  const immediateFeedbackAttempts =
    isOnline && hasImmediateFeedback
      ? Number(immediate_feedback_attempts)
      : null;

  return {
    ...advancedSettings,
    [ADVANCED_FIELD_NAMES.LOCKDOWN_BROWSER]: isSecure,
    has_immediate_feedback: hasImmediateFeedback,
    immediate_feedback_attempts: immediateFeedbackAttempts,
    scramble_questions: scramble_questions === 'scramble_questions',
    scramble_questions_per_student:
      scramble_questions === 'scramble_questions_per_student',
  };
};

export const generatePayload = (
  basicSettings,
  advancedSettings,
  activeCasSettings,
  itemId,
  isSPP,
  subjectInfo,
  timezone,
  allowHardTimerFF = false,
) => {
  const currentDateAndTime = dayjs().tz(timezone);

  const payload = {
    item_id: itemId,
    assignees: {},
  };

  const {
    starts_at,
    due_at,
    timer,
    score_display,
    print_toggle,
    allow_late_submission = false,
    is_soft_timer,
  } = basicSettings;

  const isTimerVisible = !print_toggle && !isSPP;

  const transformedAdvancedSettings = transformAdvancedSettings(
    advancedSettings,
    print_toggle,
  );

  const mainSettings = {
    starts_at: formatDateWithFallback(
      starts_at,
      currentDateAndTime,
      print_toggle,
      timezone,
      FIELD_NAMES.STARTS_AT,
    ),
    due_at: formatDateWithFallback(
      due_at,
      currentDateAndTime, // Handle the edge case where both due date and start date are in the past
      print_toggle,
      timezone,
      FIELD_NAMES.DUE_AT,
    ),
    timer: isTimerVisible ? timer : null,
    score_display,
    print_toggle,
    allow_late_submission: !isEmpty(due_at) ? allow_late_submission : false,
    /**
     * The is_hard_timer value for an assignment is the default state and therefore
     * is always true, unless the user set a timer and explicitly
     * checked the soft timer checkbox
     */
    [HARD_TIMER_FIELD]:
      isTimerVisible && timer ? !is_soft_timer : allowHardTimerFF,
    ...transformedAdvancedSettings,
  };

  /**
   * Build the payload for the sections selected in the main settings
   * if we have sections selected.
   *
   * It could be possible to not have sections selected if a CAS setting
   * section "steals" all the main students.
   */
  for (const [classId, classInfo] of Object.entries(
    basicSettings[FIELD_NAMES.CLASS_DROPDOWN],
  )) {
    const studentIds = isFullClass(subjectInfo, classId, classInfo.student_ids)
      ? []
      : classInfo.student_ids;

    const classPayload = {
      student_ids: studentIds,
      ...mainSettings,
    };

    payload.assignees[classId] = classPayload;
  }

  /**
   * Don't bother processing CAS if the teacher did a bunch of CAS
   * selections and then decided to switch to print.
   */
  if (print_toggle) return payload;

  /**
   * Append CAS settings to their respective class setting payloads
   */
  activeCasSettings.map((casSetting) => {
    for (const [classId, classInfo] of Object.entries(
      casSetting[FIELD_NAMES.CLASS_DROPDOWN],
    )) {
      const {
        starts_at: cas_startsAt,
        due_at: cas_dueAt,
        timer: cas_timer,
      } = casSetting;

      const casPayload = {
        starts_at: formatDateWithFallback(
          cas_startsAt,
          currentDateAndTime,
          print_toggle,
          timezone,
          FIELD_NAMES.STARTS_AT,
        ),
        due_at: formatDateWithFallback(
          cas_dueAt,
          currentDateAndTime,
          print_toggle,
          timezone,
          FIELD_NAMES.DUE_AT,
        ),
        timer: cas_timer,
      };

      const existingSectionSetting = get(payload.assignees, classId, null);

      /**
       * If it does't exist. This CAS setting
       * will represent the main settings for that class.
       */
      if (!existingSectionSetting) {
        const studentIds = isFullClass(
          subjectInfo,
          classId,
          classInfo.student_ids,
        )
          ? []
          : classInfo.student_ids;

        const classPayload = {
          student_ids: studentIds,
          ...mainSettings,
          ...casPayload,
        };

        payload.assignees[classId] = classPayload;
      } else {
        /**
         * A parent class setting exists, so we need to add CAS to it.
         * But first, we need to make sure the CAS settings are actually
         * different from the parent settings. If the settings are the same,
         * no need to create a CAS. We can merge them
         */
        const isCasDifferent =
          existingSectionSetting.starts_at !== casPayload.starts_at ||
          existingSectionSetting.due_at !== casPayload.due_at ||
          existingSectionSetting.timer !== casPayload.timer;

        const allStudents = [
          ...existingSectionSetting.student_ids,
          /**
           * CAS student IDs selected in the dropdown we are parsing through now.
           * If allStudents doesn't end up being all students in the section, the CAS
           * students we are processing now will end up being part of the ISA.
           */
          ...classInfo.student_ids,
        ];
        const newStudentIds = isFullClass(subjectInfo, classId, allStudents)
          ? []
          : allStudents;

        if (!isCasDifferent) {
          /**
           * If we're here, the teacher created a CAS, but didn't change the
           * setting so that it's different from the main setting. So we need
           * to merge these students into the main setting.
           */
          payload.assignees[classId] = {
            ...existingSectionSetting,
            student_ids: newStudentIds,
          };
        } else {
          /**
           * The CAS is different from the main section settings and is worthy
           * of getting a CAS object created for it.
           */
          const studentIds = isFullClass(
            subjectInfo,
            classId,
            classInfo.student_ids,
          )
            ? []
            : classInfo.student_ids;

          if (studentIds.length === 0) {
            /**
             * This should never happen. This implies the section was selected in the
             * main settings AND as a full class in a CAS setting. This should not be
             * possible or supported.
             *
             * Short ciruit this very fast.
             */
            continue;
          }

          /**
           * Add the CAS setting to it's parent payload.
           *
           * At this point we KNOW, that the CAS setting
           * 1. has a parent
           * 2. is different from the main setting
           */
          payload.assignees[classId] = {
            ...existingSectionSetting,
            student_ids: newStudentIds,
            cas: {
              ...(existingSectionSetting.cas || {}),
              ...studentIds.reduce(
                (acc, studentId) => ({
                  ...acc,
                  [studentId]: casPayload,
                }),
                {},
              ),
            },
          };
        }
      }
    }
  });

  return payload;
};

export const getClassDropdownDisabledStudents = (assignments) => {
  const disabledStudents = assignments.reduce((acc, curr) => {
    const disabledForCurrentAssignment = curr.sub_group_students.map(
      (studentId) => ({
        studentId: String(studentId),
        sectionId: String(curr.section_id),
      }),
    );
    return acc.concat(disabledForCurrentAssignment);
  }, []);
  return disabledStudents;
};

export const getUnassignableSections = (
  assignments,
  disabledStudents,
  studentsBySection,
) => {
  const disabledStudentIds = Array.from(
    disabledStudents,
    ({ studentId }) => studentId,
  );
  const unassignableSections = assignments.reduce((acc, curr) => {
    const assignableStudents = (
      get(studentsBySection, curr.section_id, []) || []
    ).filter(
      ({ id: studentId }) => !disabledStudentIds.includes(String(studentId)),
    );

    const isFullClass = (curr?.sub_group_students || []).length === 0;
    if (isFullClass || assignableStudents.length === 0) {
      acc.push(curr?.section_id);
    }

    return acc;
  }, []);
  return Array.from(new Set(unassignableSections), (sectionId) =>
    Number.parseInt(sectionId, 10),
  );
};

export const showToastNotification = ({
  subjectInfo,
  assignmentsResponse,
  studentsBySection,
}) => {
  const getAssignmentLink = (assignmentId) =>
    `/${subjectInfo.id}/assignments/progress/${assignmentId}`;

  const handleNotification = (notificationMessage, assignmentId) => {
    const assignmentLink = getAssignmentLink(assignmentId);

    toast(notificationMessage, {
      actionLink: assignmentLink,
      actionText: notificationActionText,
    });
  };

  const { assignments, title } = assignmentsResponse;

  assignments.forEach((assignment) => {
    const { sub_group_students, section_id, section_name, id } = assignment;
    const isISA = sub_group_students.length > 0;
    if (isISA) {
      const subGroupStudentsDetails = get(
        studentsBySection,
        section_id,
        [],
      ).filter((student) => sub_group_students.includes(student.id));
      const studentsText =
        sub_group_students.length === 1
          ? `${subGroupStudentsDetails[0]?.firstName} ${subGroupStudentsDetails[0]?.lastName}`
          : `${subGroupStudentsDetails.length} students`;

      const notificationMessage = `${title} assigned to ${studentsText} in ${section_name}`;
      handleNotification(notificationMessage, id);
    } else {
      const notificationMessage = `${title} assigned to ${section_name}`;
      handleNotification(notificationMessage, id);
    }
  });
};
